import type { IStackProps } from '@sixui/ui';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createSequence } from '@olivierpascal/helpers';
import { Button, IconButton, PlainTooltip, Stack } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import { ContentBlock } from '~/components/molecules/ContentBlock';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { Pagination } from '~/components/molecules/Pagination';
import { Table } from '~/components/molecules/Table';
import {
  ArticleListItem,
  ArticleListItemSkeleton,
} from '~/components/organisms/ArticleListItem';
import { usePagination } from '~/hooks/usePagination';
import { useArticlesQuery } from '~/lib/api/modules/Articles/hooks/useArticlesQuery';

const GRID_TEMPLATE_COLUMNS = '1fr repeat(3, min-content)';
const DEFAULT_SKELETON_COUNT = 3;

export type IArticleListProps = IStackProps & {
  appId: string;
  categoryId?: string;
};

export const ArticleList: React.FC<IArticleListProps> = (props) => {
  const { appId, categoryId, ...other } = props;
  const { t } = useTranslation();
  const pagination = usePagination();
  const articlesQuery = useArticlesQuery(
    {
      appId,
      body: {
        category: categoryId,
        start: pagination.start,
        limit: pagination.limit,
      },
    },
    {
      enabled: !!categoryId && pagination.limit > 0,
      keepPreviousData: true,
    },
  );

  return (
    <Stack gap={2} {...other}>
      <Stack horizontal gap={2} justify="space-between">
        <PlainTooltip
          supportingText={t('ArticleList.errors.categoryRequired')}
          disabled={!!categoryId}
        >
          <Button
            variant="text"
            icon={<FontAwesomeIcon icon={faPlus} />}
            href={
              categoryId
                ? `/apps/${appId}/articles/${categoryId}/create`
                : `/apps/${appId}/articles/create`
            }
            disabled={!categoryId}
          >
            {t('ArticleList.create-article')}
          </Button>
        </PlainTooltip>

        <IconButton
          icon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
          href={`/apps/${appId}/search`}
        />
      </Stack>

      {articlesQuery.isError ? (
        <ErrorMessage error={articlesQuery.error} />
      ) : !articlesQuery.isLoading && !articlesQuery.data?.articles?.length ? (
        <ContentBlock>
          {categoryId ? (
            <ContentBlock.Empty title={t('ArticleList.empty.title')}>
              <Button href={`/apps/${appId}/articles/${categoryId}/create`}>
                {t('ArticleList.empty.action-label')}
              </Button>
            </ContentBlock.Empty>
          ) : (
            <ContentBlock.Empty
              title={t('ArticleList.empty.title')}
              subtitle={t('ArticleList.empty.no-article-in-root')}
            />
          )}
        </ContentBlock>
      ) : (
        <ContentBlock>
          <Table gridTemplateColumns={GRID_TEMPLATE_COLUMNS}>
            {articlesQuery.isLoading || articlesQuery.isPreviousData
              ? createSequence(
                  articlesQuery.data?.articles?.length ??
                    DEFAULT_SKELETON_COUNT,
                ).map((index) => (
                  <ArticleListItemSkeleton key={index} first={index === 0} />
                ))
              : articlesQuery.data?.articles?.map((article, articleIndex) => (
                  <ArticleListItem
                    key={article._id}
                    first={articleIndex === 0}
                    article={article}
                    parentCategoryId={categoryId}
                  />
                ))}
          </Table>

          <ContentBlock.Footer>
            <Pagination
              pagination={pagination}
              total={articlesQuery.data?.total}
            />
          </ContentBlock.Footer>
        </ContentBlock>
      )}
    </Stack>
  );
};
